// ===================
// PURE MATH EV CALCULATOR
// ===================

import type { Artist, Card, GameState, GameBoard, Player } from '../../../types/game'
import type { VisibleGameState } from '../../types/knowledge'
import { CARD_DISTRIBUTION } from '../../../engine/constants'
import { ARTISTS } from '../../../engine/constants'

// ============================================================================
// PROBABILITY MODEL CONSTANTS
// ============================================================================
// These constants were calibrated based on game theory principles and empirical
// reasoning about Modern Art mechanics. All values represent probabilities
// bounded between 0 and 1.

/**
 * PAYOUT VALUES - from game rules (1st/2nd/3rd place in a round)
 */
const PAYOUT_FIRST = 30    // 1st place gets 30k
const PAYOUT_SECOND = 20   // 2nd place gets 20k
const PAYOUT_THIRD = 10    // 3rd place gets 10k
const PAYOUT_FOURTH_OR_FIFTH = 0  // 4th/5th get nothing

/**
 * DOMINANT POSITION PROBABILITY
 * When an artist has 4+ cards played, they're extremely likely to win.
 * Rationale: Only 5 artists exist, and 4 cards is a dominant position.
 * Even if all remaining cards of one artist are played, max would be 4-5.
 * Value: 0.96 = 96% confident (leaves small margin for tie-breaker scenarios)
 */
const PROB_DOMINANT_FOUR_CARD = 0.96

/**
 * EXPONENTIAL CONFIDENCE FORMULA (when ahead)
 * Formula: base = BASE_CONFIDENCE + (1 - e^(-lead * DECAY_RATE)) * CONFIDENCE_RANGE
 *
 * Lead of 1 → 0.78 probability
 * Lead of 2 → 0.87 probability
 * Lead of 3 → 0.93 probability
 *
 * Rationale for constants:
 * - BASE_CONFIDENCE (0.69): Even with minimal lead, have ~70% chance to maintain
 * - DECAY_RATE (0.58): Controls how quickly confidence increases with lead size
 *   Higher = faster confidence growth, lower = more gradual
 *   Chosen so lead of 2 gives ~87% (strong but not certain)
 * - CONFIDENCE_RANGE (0.27): Maximum boost from exponential term
 *   Ensures probability approaches ~0.96 as lead increases
 */
const BASE_CONFIDENCE = 0.69
const DECAY_RATE = 0.58
const CONFIDENCE_RANGE = 0.27

/**
 * HAND CONTROL BONUS (when ahead)
 * Having cards in hand means you can influence the outcome.
 * Bonus scales with hand size but has diminishing returns.
 *
 * Formula: HAND_BONUS_MAX * min(1, hand / HAND_BONUS_SCALING)
 *
 * Rationale:
 * - HAND_BONUS_MAX (0.04): Maximum 4% boost for significant hand control
 *   Small because current lead is already the dominant factor
 * - HAND_BONUS_SCALING (1.5): Need ~1-2 cards to reach max bonus
 *   Represents diminishing returns - 3 cards isn't much better than 2
 */
const HAND_BONUS_MAX = 0.04
const HAND_BONUS_SCALING = 1.5

/**
 * TOTAL CARD ADVANTAGE BONUS (when ahead)
 * Small bonus for having more total cards in existence (played + remaining).
 * This is a weak signal because remaining cards may never be played.
 *
 * Formula: TOTAL_BONUS_MAX * min(1, advantage / TOTAL_BONUS_THRESHOLD)
 *
 * Rationale:
 * - TOTAL_BONUS_MAX (0.015): Maximum 1.5% boost - very small advantage
 * - TOTAL_BONUS_THRESHOLD (5): Need 5+ more total cards to max out
 */
const TOTAL_BONUS_MAX = 0.015
const TOTAL_BONUS_THRESHOLD = 5

/**
 * GAP PROBABILITIES (when behind)
 * When behind by gap cards, probability depends heavily on hand control.
 * Without hand control, you rely on others not to play their cards.
 *
 * LARGE GAP (3+ cards behind):
 * - PROB_GAP_LARGE_NO_HAND (0.015): ~1.5% chance with no hand
 *   Almost impossible - opponents would need to pass many times
 * - PROB_GAP_LARGE_WITH_HAND (0.045): ~4.5% chance with some hand
 * - PROB_GAP_LARGE_WITH_ENOUGH_HAND (0.15): 15% chance if hand >= gap-1
 *   Catching up from 3+ cards behind is very difficult
 *
 * MEDIUM GAP (2 cards behind):
 * - PROB_GAP_MEDIUM_NO_HAND (0.06): ~6% chance with no hand
 *   Very low but not impossible
 * - PROB_GAP_MEDIUM_WITH_HAND (0.18): 18% chance with hand
 * - PROB_GAP_MEDIUM_WITH_GOOD_HAND (0.32): 32% chance if hand >=2 and total advantage
 *   Can catch up if have 2+ cards to play
 * - PROB_GAP_MEDIUM_HAND_BOOST (0.10): +10% for having at least 1 card
 *
 * SMALL GAP (1 card behind):
 * - PROB_GAP_SMALL_NO_HAND (0.18): 18% chance with no hand
 *   Competitive but disadvantaged
 * - PROB_GAP_SMALL_WITH_HAND (0.44): 44% chance with hand
 *   Slightly below even because of current disadvantage
 * - PROB_GAP_SMALL_TOTAL_ADVANTAGE (0.06): +6% if have more total cards
 * - PROB_GAP_SMALL_LARGE_HAND (0.06): +6% for 2+ cards in hand
 */
const PROB_GAP_LARGE_NO_HAND = 0.015
const PROB_GAP_LARGE_WITH_HAND = 0.045
const PROB_GAP_LARGE_WITH_ENOUGH_HAND = 0.15
const PROB_GAP_MEDIUM_NO_HAND = 0.06
const PROB_GAP_MEDIUM_WITH_HAND = 0.18
const PROB_GAP_MEDIUM_WITH_GOOD_HAND = 0.32
const PROB_GAP_MEDIUM_HAND_BOOST = 0.10
const PROB_GAP_SMALL_NO_HAND = 0.18
const PROB_GAP_SMALL_WITH_HAND = 0.44
const PROB_GAP_SMALL_TOTAL_ADVANTAGE = 0.06
const PROB_GAP_SMALL_LARGE_HAND = 0.06

/**
 * TIED POSITION PROBABILITIES
 * When tied, hand control is the deciding factor.
 *
 * Rationale:
 * - PROB_TIED_NO_HAND (0.44): 44% chance without hand control
 *   Slight disadvantage because opponent might have cards
 * - PROB_TIED_BASE (0.57): 57% base with at least 1 card in hand
 *   Having a card to play gives meaningful advantage
 * - PROB_TIED_HAND_SCALING (0.10): Up to +10% for multiple cards
 * - PROB_TIED_HAND_SCALING_THRESHOLD (2): Need 2 cards to max out
 * - PROB_TIED_TOTAL_ADVANTAGE (0.06): ±6% for total card advantage/disadvantage
 *   Only applies if advantage is 2+ cards (significant difference)
 * - PROB_TIED_TOTAL_THRESHOLD (2): Threshold for "significant" total difference
 */
const PROB_TIED_NO_HAND = 0.44
const PROB_TIED_BASE = 0.57
const PROB_TIED_HAND_SCALING = 0.10
const PROB_TIED_HAND_SCALING_THRESHOLD = 2
const PROB_TIED_TOTAL_ADVANTAGE = 0.06
const PROB_TIED_TOTAL_THRESHOLD = 2

/**
 * RANK PROBABILITY APPROXIMATION
 * Converting "beat each competitor" probabilities to rank probabilities.
 *
 * NOTE: This is an approximation. Exact calculation would require considering
 * all permutations of 5 artists finishing order, which is computationally
 * expensive and not necessarily more accurate given incomplete information.
 *
 * We use the independence assumption because:
 * 1. We have no visibility into opponents' hands
 * 2. We cannot predict opponent strategy
 * 3. Card draws are functionally random from AI's perspective
 *
 * Formula for P(2nd) and P(3rd):
 * - SECOND_PLACE_MULTIPLIER (0.7): 70% of avg beat probability for 2nd
 * - THIRD_PLACE_MULTIPLIER (0.5): 50% of avg beat probability for 3rd
 *
 * Rationale: These are conservative estimates that roughly approximate
 * the probability of beating most but not all competitors.
 */
const SECOND_PLACE_MULTIPLIER = 0.7
const THIRD_PLACE_MULTIPLIER = 0.5

/**
 * BOARD POSITION BONUS
 * When artists are tied in card count, board position affects winning probability.
 * Board order: Solstice (1st) > Mira (2nd) > Atlas (3rd) > Vesper (4th) > Luxe (5th)
 * Artists to the left have an advantage due to tiebreaker rules.
 *
 * Rationale:
 * - BOARD_POSITION_BONUS_MULTIPLIER (0.25): 25% bonus per position unit when ahead
 * - BOARD_POSITION_PENALTY_MULTIPLIER (0.15): 15% penalty per position unit when behind
 *   Asymmetric because having advantage is more impactful than disadvantage
 *
 * Example: Atlas (position 3) vs Solstice (position 1):
 *   positionAdvantage = (1 - 3) / 4 = -0.5
 *   Penalty = -0.5 × 0.15 = -0.075 (7.5% reduction in beat probability)
 */
const BOARD_POSITION_BONUS_MULTIPLIER = 0.25
const BOARD_POSITION_PENALTY_MULTIPLIER = 0.15

/**
 * HARVILLE MODEL THRESHOLD
 * Minimum total cards played before switching from multiplier approach to Harville model.
 *
 * Rationale:
 * - Early game (< 6 cards total): Harville model can be overconfident due to limited data
 * - Mid/late game (≥ 6 cards total): Harville model provides mathematically sound probabilities
 * - With 5 artists, 6 cards = ~1.2 cards per artist on average (still early game)
 * - This threshold was calibrated to balance between accuracy and conservatism
 */
const HARVILLE_MODEL_THRESHOLD_CARDS = 6

/**
 * PROBABILITY BOUNDS
 * Clamp all probabilities to valid range.
 * - MIN_PROB (0.01): Minimum 1% chance (nothing is truly impossible)
 * - MAX_PROB (0.985): Maximum 98.5% chance (nothing is truly certain)
 *   Leaves room for edge cases and unexpected game states
 */
const MIN_PROB = 0.01
const MAX_PROB = 0.985

/**
 * EV ROUNDING THRESHOLD
 * Threshold for rounding EV values to integers.
 *
 * Rationale:
 * - Standard rounding uses 0.5 threshold, but EV values are probabilistic estimates
 * - Using 0.7 provides a more conservative bias (rounds down more often)
 * - This aligns with the cautious betting strategy: prefer underestimating to overestimating
 * - Example: EV 17.71 → 18 (confident it's worth 18+), but EV 17.45 → 17 (less confident)
 */
const EV_ROUNDING_THRESHOLD = 0.7

// ============================================================================
// PROBABILITY MODEL END - Helper Functions Below
// ============================================================================

/**
 * Round EV value using custom rounding rule
 * If decimal >= EV_ROUNDING_THRESHOLD, round up; otherwise round down
 *
 * Examples:
 * - 21.98 → 22 (0.98 >= 0.7, round up)
 * - 17.45 → 17 (0.45 < 0.7, round down)
 * - 22.39 → 22 (0.39 < 0.7, round down)
 * - 17.71 → 18 (0.71 >= 0.7, round up)
 */
function roundEV(value: number): number {
  // No-op: return raw EV value without rounding
  return value
}

/**
 * Result of EV calculation
 */
export interface CardEVResult {
  /** Total expected value (cumulative + this round) */
  total: number
  /** Sum of values from previous rounds (deterministic) */
  previousCumulative: number
  /** Expected value for THIS ROUND only (probabilistic) */
  thisRound: number
  /** Probabilities for each rank this round */
  probabilities: {
    first: number      // P(1st place)
    second: number     // P(2nd place)
    third: number      // P(3rd place)
    fourthOrFifth: number // P(4th or 5th place)
  }
  /** Minimum possible final count (played + in hand) */
  minPossible: number
  /** Maximum possible final count (played + in hand + remaining) */
  maxPossible: number
  /** Current rank (1-5) */
  currentRank: number
}

/**
 * Competitor information for placement calculation
 */
export interface CompetitorInfo {
  artist: Artist
  minPossible: number  // Guaranteed minimum (played + in hand)
  maxPossible: number  // Theoretical maximum (played + in hand + remaining)
}

/**
 * Guaranteed placement bounds result
 */
export interface PlacementBounds {
  bestCase: 1 | 2 | 3 | 4 | 5      // Best possible placement (if I max out, others min out)
  worstCase: 1 | 2 | 3 | 4 | 5     // Worst possible placement (if I min out, others max out)
  cannotFinishWorseThan: 1 | 2 | 3 | 4 | 5 | null  // Guaranteed placement floor (null if no guarantee)
}

// ============================================================================
// GUARANTEED PLACEMENT BOUNDS CALCULATOR
// ============================================================================

/**
 * Calculate guaranteed placement bounds based on stack ranking hierarchy
 *
 * This function determines the best and worst possible placements for an artist,
 * accounting for the deterministic tie-breaking rules:
 * - Ties are broken by board position (leftmost artist wins)
 * - Board order: Solstice (1st) > Mira (2nd) > Atlas (3rd) > Vesper (4th) > Luxe (5th)
 *
 * KEY INSIGHT:
 * An artist's guaranteed placement depends on:
 * 1. Their minimum guaranteed card count (played + in hand)
 * 2. How many artists with BETTER board position could match/beat that count
 * 3. The maximum possible counts of all competitors
 *
 * EXAMPLE 1: Solstice with 4 guaranteed cards
 * - Best case: Nobody else reaches 4 → 1st place
 * - Worst case: One artist reaches 5 → 2nd place (Solstice wins all ties at 4)
 * - Cannot finish worse than: 2nd place
 *
 * EXAMPLE 2: Luxe with 4 guaranteed cards
 * - Best case: Nobody else reaches 4 → 1st place
 * - Worst case: One artist has 5, three others have 4 → 5th place (Luxe loses all ties)
 * - Cannot finish worse than: 5th place (no guarantee)
 *
 * @param myArtist - The artist we're calculating for
 * @param myMinPossible - My guaranteed minimum count (played + in hand)
 * @param myMaxPossible - My theoretical maximum count (played + in hand + remaining)
 * @param competitors - Array of competitor info with their min/max possible counts
 * @returns PlacementBounds with best case, worst case, and guaranteed floor
 */
export function calculateGuaranteedPlacementBounds(
  myArtist: Artist,
  myMinPossible: number,
  myMaxPossible: number,
  competitors: CompetitorInfo[]
): PlacementBounds {
  const myBoardPosition = ARTISTS.indexOf(myArtist)

  // ============================================
  // BEST CASE: I get maxPossible, others get minPossible
  // ============================================
  const bestCaseRanking: Array<{ artist: Artist; count: number; boardPosition: number }> = [
    { artist: myArtist, count: myMaxPossible, boardPosition: myBoardPosition },
    ...competitors.map(c => ({
      artist: c.artist,
      count: c.minPossible,
      boardPosition: ARTISTS.indexOf(c.artist)
    }))
  ]

  // Sort by count (desc), then board position (asc for tie-breaking)
  bestCaseRanking.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count
    return a.boardPosition - b.boardPosition
  })

  const bestCase = (bestCaseRanking.findIndex(r => r.artist === myArtist) + 1) as 1 | 2 | 3 | 4 | 5

  // ============================================
  // WORST CASE: I get minPossible, others get maxPossible
  // ============================================
  const worstCaseRanking: Array<{ artist: Artist; count: number; boardPosition: number }> = [
    { artist: myArtist, count: myMinPossible, boardPosition: myBoardPosition },
    ...competitors.map(c => ({
      artist: c.artist,
      count: c.maxPossible,
      boardPosition: ARTISTS.indexOf(c.artist)
    }))
  ]

  // Sort by count (desc), then board position (asc for tie-breaking)
  worstCaseRanking.sort((a, b) => {
    if (b.count !== a.count) return b.count - a.count
    return a.boardPosition - b.boardPosition
  })

  const worstCase = (worstCaseRanking.findIndex(r => r.artist === myArtist) + 1) as 1 | 2 | 3 | 4 | 5

  // ============================================
  // GUARANTEED FLOOR: What's the worst I can POSSIBLY finish?
  // ============================================
  // Key insight: My worst-case placement = 1 + (# who beat me) + (# who tie with better position)
  // In the WORST case scenario:
  // - All competitors reach their maxPossible
  // - If their maxPossible > myMinPossible: they BEAT me
  // - If their maxPossible == myMinPossible: they TIE me (board position decides)
  // - If their maxPossible < myMinPossible: they LOSE to me
  //
  // Example: Solstice with 4 guaranteed
  // - If 1 artist maxPossible=5: beats = 1, ties = 0 → worst = 2nd place
  // - If all 4 artists maxPossible=4: beats = 0, ties = 0 (Solstice wins all ties) → worst = 1st place
  //
  // Example: Luxe with 4 guaranteed
  // - If 1 artist maxPossible=5, 3 artists maxPossible=4: beats = 1, ties = 3 → worst = 5th place

  // Count competitors who BEAT me (their max > my min)
  const competitorsThatBeatMe = competitors.filter(
    c => c.maxPossible > myMinPossible
  ).length

  // Count competitors who TIE me with better board position (their max == my min)
  const competitorsThatTieWithBetterPosition = competitors.filter(
    c => c.maxPossible === myMinPossible && ARTISTS.indexOf(c.artist) < myBoardPosition
  ).length

  // Calculate worst-case placement
  const worstCasePlacement = 1 + competitorsThatBeatMe + competitorsThatTieWithBetterPosition

  // Clamp to 1-5 range, return null only if worse than 5th
  let cannotFinishWorseThan: 1 | 2 | 3 | 4 | 5 | null
  if (worstCasePlacement > 5) {
    cannotFinishWorseThan = null // No guarantee (could finish anywhere)
  } else {
    cannotFinishWorseThan = Math.max(1, worstCasePlacement) as 1 | 2 | 3 | 4 | 5
  }

  return {
    bestCase,
    worstCase,
    cannotFinishWorseThan
  }
}

/**
 * Pure mathematical Expected Value calculator
 *
 * Uses probability theory based on card counts with calibrated constants.
 * The model makes reasonable assumptions given incomplete information:
 * - Opponent hands are unknown (treated as random)
 * - Opponent strategy cannot be predicted
 * - Card availability is the primary factor in artist success
 *
 * This is the best possible approach without clairvoyance.
 */
export class PureMathEVCalculator {
  /**
   * Calculate expected value for a specific artist card
   * Overload for GameState (used in tests)
   *
   * Formula: EV = Previous Cumulative + EV This Round
   * - Previous Cumulative: Sum of all values from previous rounds (deterministic)
   * - EV This Round: P(1st)×30 + P(2nd)×20 + P(3rd)×10 + P(4th/5th)×0
   */
  calculateCardEV(
    artist: Artist,
    gameState: GameState,
    aiHand: Card[]
  ): CardEVResult

  /**
   * Calculate expected value for a specific artist card
   * Overload for VisibleGameState (used in AI bidding)
   */
  calculateCardEV(
    artist: Artist,
    gameState: VisibleGameState,
    aiHand: Card[]
  ): CardEVResult

  /**
   * Implementation of calculateCardEV
   */
  calculateCardEV(
    artist: Artist,
    gameState: GameState | VisibleGameState,
    aiHand: Card[]
  ): CardEVResult {
    // Extract round number (handle both GameState and VisibleGameState)
    const roundNumber = 'round' in gameState ? gameState.round.roundNumber : gameState.roundNumber

    // Check if this is a double auction (2 cards being sold together)
    const isDoubleAuction = this.isDoubleAuction(gameState)

    // 1. Get previous cumulative value (deterministic)
    const previousCumulative = this.getPreviousCumulativeValue(
      gameState.board,
      artist,
      roundNumber
    )

    // 2. Calculate EV for THIS ROUND (probabilistic)
    const thisRoundResult = isDoubleAuction
      ? this.calculateDoubleAuctionEV(artist, gameState, aiHand, roundNumber)
      : this.calculateCurrentRoundEV(artist, gameState, aiHand, roundNumber)

    // 3. Total = cumulative + this round
    const total = previousCumulative + thisRoundResult.ev

    return {
      total: roundEV(total),
      previousCumulative,
      thisRound: roundEV(thisRoundResult.ev),
      probabilities: thisRoundResult.probabilities,
      minPossible: thisRoundResult.minPossible,
      maxPossible: thisRoundResult.maxPossible,
      currentRank: thisRoundResult.currentRank,
    }
  }

  /**
   * Check if the current auction is a double auction (2 cards being sold together)
   */
  private isDoubleAuction(gameState: GameState | VisibleGameState): boolean {
    // Check if we're in an auction phase
    const phase = 'round' in gameState ? gameState.round.phase : gameState.phase
    if (phase.type !== 'auction') {
      return false
    }

    // Check if the auction has 2 cards
    const auction = phase.auction
    if (!auction) {
      return false
    }

    // Check for embedded auction (double auction case)
    if (auction.type === 'double' && auction.embeddedAuction) {
      return auction.embeddedAuction.cards?.length === 2
    }

    // Check for cards array directly on auction
    return auction.cards?.length === 2
  }

  /**
   * Calculate EV for double auction (2 cards being sold together)
   * Simulates adding 2 cards to the board instead of 1
   */
  private calculateDoubleAuctionEV(
    artist: Artist,
    gameState: GameState | VisibleGameState,
    aiHand: Card[],
    roundNumber: number
  ): {
    ev: number
    probabilities: CardEVResult['probabilities']
    minPossible: number
    maxPossible: number
    currentRank: number
  } {
    // Get current state
    const currentPlayed = (gameState.board.playedCards[artist] || []).length
    const inHand = aiHand.filter(c => c.artist === artist).length

    // For double auction, we're getting 2 cards, so add 2 to the count
    const cardsBeingAuctioned = 2
    const newPlayedCount = currentPlayed + cardsBeingAuctioned

    // Calculate min/max possible with 2 additional cards
    const totalInDeck = CARD_DISTRIBUTION[artist]
    const remaining = totalInDeck - currentPlayed - inHand
    const minPossible = newPlayedCount + inHand
    const maxPossible = newPlayedCount + inHand + remaining

    // Get current rank (before adding cards)
    const currentRank = this.getCurrentRank(artist, gameState)

    // Get competitor information
    const competitors = this.getCompetitorStates(artist, gameState)

    // Calculate probabilities for each rank with 2 additional cards
    const probabilities = this.calculateRankProbabilitiesForDouble(
      artist,
      currentPlayed,
      inHand,
      remaining,
      competitors,
      minPossible,
      maxPossible
    )

    // Calculate EV using payout values
    const ev =
      probabilities.first * PAYOUT_FIRST +
      probabilities.second * PAYOUT_SECOND +
      probabilities.third * PAYOUT_THIRD +
      probabilities.fourthOrFifth * PAYOUT_FOURTH_OR_FIFTH

    return {
      ev: Math.max(0, ev),
      probabilities,
      minPossible,
      maxPossible,
      currentRank,
    }
  }

  /**
   * Calculate rank probabilities - unified function for single and double auctions
   *
   * This function handles both regular (+1 card) and double (+2 cards) auctions
   * by using a cardsFromAuction parameter. It calculates the probability of finishing
   * in each place (1st through 5th) based on current board state.
   *
   * @param artist - The artist we're calculating EV for
   * @param currentPlayed - Number of this artist's cards currently on the board
   * @param inHand - Number of this artist's cards in hand (not yet played)
   * @param remaining - Number of this artist's cards remaining in deck
   * @param competitors - Array of competitor artists with their current/max counts
   * @param minPossible - Minimum possible final count (current + inHand)
   * @param maxPossible - Maximum possible final count (current + inHand + remaining)
   * @param cardsFromAuction - Number of cards being added from this auction (1 or 2)
   * @returns Placement probabilities that sum to 1.0
   */
  private calculateRankProbabilitiesUnified(
    artist: Artist,
    currentPlayed: number,
    inHand: number,
    remaining: number,
    competitors: Array<{ artist: Artist; currentCount: number; maxPossible: number }>,
    minPossible: number,
    maxPossible: number,
    cardsFromAuction: 1 | 2
  ): CardEVResult['probabilities'] {
    // Calculate probability of beating each competitor individually
    // P(I beat C) = probability my final count > their final count
    const probabilitiesOfBeating = competitors.map(competitor => {
      // Use the appropriate probability calculation based on cards from auction
      return cardsFromAuction === 2
        ? this.calculateProbabilityOfBeatingWithDouble(
            currentPlayed,
            inHand,
            remaining,
            competitor.currentCount,
            competitor.maxPossible - competitor.currentCount,
            artist,
            competitor.artist
          )
        : this.calculateProbabilityOfBeating(
            currentPlayed,
            inHand,
            remaining,
            competitor.currentCount,
            competitor.maxPossible - competitor.currentCount,
            artist,
            competitor.artist
          )
    })

    // ============================================================
    // HYBRID APPROACH: Early game uses multipliers, mid/late uses Harville
    // ============================================================
    // Early game (< HARVILLE_MODEL_THRESHOLD_CARDS total cards played) is highly volatile
    // Harville overestimates confidence when sample size is small
    // Use multiplier approach for early game, Harville for mid/late game
    const totalCardsPlayed = competitors.reduce((sum, c) => sum + c.currentCount, 0) + currentPlayed

    let first: number, second: number, third: number, fourthOrFifth: number

    if (totalCardsPlayed < HARVILLE_MODEL_THRESHOLD_CARDS) {
      // Early game: Use multiplier approach (more conservative)
      first = probabilitiesOfBeating.reduce((prod, p) => prod * p, 1.0)
      const avgBeatProb = probabilitiesOfBeating.reduce((a, b) => a + b, 0) / probabilitiesOfBeating.length
      second = Math.max(0, avgBeatProb * SECOND_PLACE_MULTIPLIER - first)
      third = Math.max(0, avgBeatProb * THIRD_PLACE_MULTIPLIER - first - second)
      fourthOrFifth = Math.max(0, 1 - first - second - third)
    } else {
      // Mid/late game: Use Harville model (more accurate)
      const harvilleProbs = this.calculateRankProbabilitiesHarville(probabilitiesOfBeating)
      first = harvilleProbs.first
      second = harvilleProbs.second
      third = harvilleProbs.third
      fourthOrFifth = harvilleProbs.fourthOrFifth
    }

    // ============================================================
    // APPLY GUARANTEED PLACEMENT BOUNDS
    // ============================================================
    // Note: minPossible/maxPossible already account for cards from auction
    const competitorInfos: CompetitorInfo[] = competitors.map(c => ({
      artist: c.artist,
      minPossible: c.currentCount,  // Worst case: they don't play any more cards
      maxPossible: c.maxPossible     // Best case: they play all remaining cards
    }))

    const placementBounds = calculateGuaranteedPlacementBounds(
      artist,
      minPossible,
      maxPossible,
      competitorInfos
    )

    // Apply constraints based on guaranteed placement floor
    // SPECIAL CASE: minPossible >= 5 means round ENDS when we play
    // This is a game rule constraint, not just stack ranking
    if (minPossible >= 5) {
      // Guaranteed to end the round = guaranteed 1st or tied for 1st
      const redistribution = second + third + fourthOrFifth
      first = Math.min(MAX_PROB, first + redistribution)
      second = 0
      third = 0
      fourthOrFifth = 0
    } else if (placementBounds.cannotFinishWorseThan !== null) {
      const guaranteedFloor = placementBounds.cannotFinishWorseThan

      if (guaranteedFloor <= 1) {
        // Guaranteed 1st place - redistribute all probability to 1st
        const redistribution = second + third + fourthOrFifth
        first = Math.min(MAX_PROB, first + redistribution)
        second = 0
        third = 0
        fourthOrFifth = 0
      } else if (guaranteedFloor <= 2) {
        // Guaranteed 2nd or better - zero out 3rd/4th/5th
        const redistribution = third + fourthOrFifth
        first = Math.min(MAX_PROB, first + redistribution * 0.7)
        second = Math.min(MAX_PROB, second + redistribution * 0.3)
        third = 0
        fourthOrFifth = 0
      } else if (guaranteedFloor <= 3) {
        // Guaranteed 3rd or better - zero out 4th/5th
        const redistribution = fourthOrFifth
        first = Math.min(MAX_PROB, first + redistribution * 0.5)
        second = Math.min(MAX_PROB, second + redistribution * 0.3)
        third = Math.min(MAX_PROB, third + redistribution * 0.2)
        fourthOrFifth = 0
      } else if (guaranteedFloor <= 4) {
        // Guaranteed 4th or better - cap 4th/5th based on how strong the position is
        // If minPossible >= 4, this is a VERY strong guarantee (only 3 artists score)
        let maxFourthOrFifth: number
        if (minPossible >= 4) {
          maxFourthOrFifth = 0.01  // Very small probability for strong positions
        } else {
          maxFourthOrFifth = 0.15  // More generous for weaker positions
        }

        if (fourthOrFifth > maxFourthOrFifth) {
          const redistribution = fourthOrFifth - maxFourthOrFifth
          first += redistribution * 0.5
          second += redistribution * 0.3
          third += redistribution * 0.2
          fourthOrFifth = maxFourthOrFifth
        }
      }
      // If guaranteedFloor == 5, no constraint needed (could finish anywhere)
    }

    // Additional constraint: minPossible >= 4 should have very low P(4th/5th)
    // This accounts for the "5 cards ends round" rule which placement bounds doesn't consider
    if (minPossible >= 4 && fourthOrFifth > 0.01) {
      const maxAllowed = 0.01
      const redistribution = fourthOrFifth - maxAllowed
      first += redistribution * 0.5
      second += redistribution * 0.3
      third += redistribution * 0.2
      fourthOrFifth = maxAllowed
    }

    // Normalize to ensure probabilities sum to 1.0
    const sum = first + second + third + fourthOrFifth
    if (sum > 0) {
      first = first / sum
      second = second / sum
      third = third / sum
      fourthOrFifth = fourthOrFifth / sum
    }

    return {
      first: Math.max(0, Math.min(1, first)),
      second: Math.max(0, Math.min(1, second)),
      third: Math.max(0, Math.min(1, third)),
      fourthOrFifth: Math.max(0, Math.min(1, fourthOrFifth)),
    }
  }

  /**
   * Calculate rank probabilities for double auction (adding 2 cards)
   *
   * This is a wrapper function that calls the unified calculateRankProbabilitiesUnified
   * with cardsFromAuction=2 to handle double auction scenarios.
   *
   * @param artist - The artist we're calculating EV for
   * @param currentPlayed - Number of this artist's cards currently on the board
   * @param inHand - Number of this artist's cards in hand (not yet played)
   * @param remaining - Number of this artist's cards remaining in deck
   * @param competitors - Array of competitor artists with their current/max counts
   * @param minPossible - Minimum possible final count (current + inHand + 2 from auction)
   * @param maxPossible - Maximum possible final count (current + inHand + remaining + 2 from auction)
   * @returns Placement probabilities that sum to 1.0
   */
  private calculateRankProbabilitiesForDouble(
    artist: Artist,
    currentPlayed: number,
    inHand: number,
    remaining: number,
    competitors: Array<{ artist: Artist; currentCount: number; maxPossible: number }>,
    minPossible: number,
    maxPossible: number
  ): CardEVResult['probabilities'] {
    // For double auction, we simulate adding 2 cards to our count
    // This gives us a significant advantage in beating competitors
    // The unified function handles both single (+1) and double (+2) auction cases
    return this.calculateRankProbabilitiesUnified(
      artist,
      currentPlayed,
      inHand,
      remaining,
      competitors,
      minPossible,
      maxPossible,
      2  // cardsFromAuction = 2 for double auction
    )
  }

  /**
   * Calculate rank probabilities using the Harville model
   *
   * The Harville model is a well-established probability model used in horse racing
   * to calculate placement probabilities. It treats each competitor as having a "strength"
   * and calculates placement probabilities by considering all possible orderings.
   *
   * Mathematical foundation:
   * - Convert beat probabilities to strength ratios: strength = p / (1 - p)
   * - P(1st) = my strength / total strength
   * - P(2nd) = Sum over each competitor: P(they get 1st) × P(I get 1st among remaining)
   * - P(3rd) = Sum over all pairs: P(A gets 1st) × P(B gets 2nd) × P(I get 1st among remaining)
   *
   * Reference: Harville, D. A. (1973). "Assigning Probabilities to the Outcomes of Multi-Entry
   * Competitions." Journal of the American Statistical Association, 68(342), 312-316.
   *
   * @param probabilitiesOfBeating - Array of probabilities of beating each competitor
   * @returns Placement probabilities that always sum to 1.0 with no negative values
   */
  private calculateRankProbabilitiesHarville(
    probabilitiesOfBeating: number[]
  ): CardEVResult['probabilities'] {
    const n = probabilitiesOfBeating.length

    // If no competitors, guaranteed 1st place
    if (n === 0) {
      return { first: 1, second: 0, third: 0, fourthOrFifth: 0 }
    }

    // Convert beat probabilities to "strength" values
    // If P(I beat A) = p, then A's relative strength = (1 - p) / p
    // This represents: odds of them beating me vs me beating them
    // Example: If P(I beat A) = 0.81, then their strength = (1 - 0.81) / 0.81 = 0.23
    const competitorStrengths = probabilitiesOfBeating.map(p => {
      // Clamp p to avoid division by zero or infinity
      const clampedP = Math.max(0.01, Math.min(0.99, p))
      return (1 - clampedP) / clampedP
    })

    // My strength is the reference point (1.0)
    const myStrength = 1.0

    // Calculate total strength of all competitors
    const totalCompetitorStrength = competitorStrengths.reduce((a, b) => a + b, 0)
    const totalStrength = myStrength + totalCompetitorStrength

    // P(1st) = my strength / total strength
    let first = myStrength / totalStrength

    // P(2nd) = sum over each competitor winning first, then me winning among remaining
    let second = 0
    for (let i = 0; i < n; i++) {
      const s = competitorStrengths[i]
      // Probability this competitor wins 1st
      const probTheyWinFirst = s / totalStrength
      // Probability I win among the remaining competitors (excluding this one)
      const remainingStrength = totalStrength - s
      const probIWinAmongRemaining = myStrength / remainingStrength
      second += probTheyWinFirst * probIWinAmongRemaining
    }

    // P(3rd) = sum over all pairs of competitors winning 1st and 2nd, then me winning among remaining
    let third = 0
    for (let i = 0; i < n; i++) {
      for (let j = 0; j < n; j++) {
        if (i === j) continue  // Can't have same competitor in both positions

        const s1 = competitorStrengths[i]
        const s2 = competitorStrengths[j]

        // Probability competitor i wins 1st
        const probCompetitorIWinsFirst = s1 / totalStrength
        // Probability competitor j wins 2nd given i won 1st
        const probCompetitorJWinsSecond = s2 / (totalStrength - s1)
        // Probability I win among remaining
        const remainingStrength = totalStrength - s1 - s2
        const probIWinAmongRemaining = myStrength / remainingStrength

        third += probCompetitorIWinsFirst * probCompetitorJWinsSecond * probIWinAmongRemaining
      }
    }

    // P(4th/5th) = 1 - P(1st) - P(2nd) - P(3rd)
    let fourthOrFifth = 1 - first - second - third

    // Clamp to valid probability range and ensure non-negative
    first = Math.max(0, Math.min(1, first))
    second = Math.max(0, Math.min(1, second))
    third = Math.max(0, Math.min(1, third))
    fourthOrFifth = Math.max(0, Math.min(1, fourthOrFifth))

    // Normalize to ensure they sum to exactly 1.0
    const sum = first + second + third + fourthOrFifth
    if (sum > 0) {
      first = first / sum
      second = second / sum
      third = third / sum
      fourthOrFifth = fourthOrFifth / sum
    }

    return { first, second, third, fourthOrFifth }
  }

  /**
   * Calculate probability that I beat a competitor with 2 extra cards (double auction)
   * Similar to calculateProbabilityOfBeating but assumes +2 cards instead of +1
   */
  private calculateProbabilityOfBeatingWithDouble(
    myCurrent: number,
    myInHand: number,
    myRemaining: number,
    theirCurrent: number,
    theirRemaining: number,
    myArtist: Artist,
    theirArtist: Artist
  ): number {
    // With double auction, we get 2 cards instead of 1
    const cardsFromAuction = 2
    const myCurrentWithDouble = myCurrent + cardsFromAuction
    const myMin = myCurrentWithDouble + myInHand
    const myMax = myCurrentWithDouble + myInHand + myRemaining

    const theirMin = theirCurrent
    const theirMax = theirCurrent + theirRemaining

    // Boundary conditions
    if (myMin > theirMax) return 1.0
    if (myMax < theirMin) return 0.0

    // Calculate advantage with 2 extra cards
    const currentAdvantage = myCurrentWithDouble - theirCurrent
    const totalAdvantage = myMax - theirMax
    const gap = theirCurrent - myCurrentWithDouble

    let probability: number

    if (currentAdvantage > 0) {
      // AHEAD with 2 extra cards
      if (myCurrentWithDouble >= 4) {
        probability = PROB_DOMINANT_FOUR_CARD
      } else {
        const baseProb = BASE_CONFIDENCE + (1 - Math.exp(-currentAdvantage * DECAY_RATE)) * CONFIDENCE_RANGE
        const handBonus = myInHand > 0
          ? HAND_BONUS_MAX * Math.min(1, myInHand / HAND_BONUS_SCALING)
          : 0
        const totalBonus = totalAdvantage > 0 ? TOTAL_BONUS_MAX * Math.min(1, totalAdvantage / TOTAL_BONUS_THRESHOLD) : 0
        probability = baseProb + handBonus + totalBonus
      }
    } else if (currentAdvantage < 0) {
      // BEHIND (but with 2 extra cards to catch up)
      if (gap >= 3) {
        // Even with +2, still far behind
        if (myInHand === 0) {
          probability = PROB_GAP_LARGE_NO_HAND
        } else {
          probability = PROB_GAP_LARGE_WITH_HAND
          if (myInHand >= gap - cardsFromAuction && myMax > theirMax) {
            probability = PROB_GAP_LARGE_WITH_ENOUGH_HAND
          }
        }
      } else if (gap === 2) {
        // Behind by 2, but getting +2 cards = tied
        if (myInHand === 0) {
          probability = PROB_GAP_MEDIUM_NO_HAND
        } else {
          probability = PROB_GAP_MEDIUM_WITH_HAND
          if (myInHand >= 2 && myMax > theirMax) {
            probability = PROB_GAP_MEDIUM_WITH_GOOD_HAND
          } else if (myInHand >= 1) {
            probability += PROB_GAP_MEDIUM_HAND_BOOST
          }
        }
      } else {
        // Small gap (1 card behind), getting +2 = ahead by 1
        if (myInHand === 0) {
          probability = PROB_GAP_SMALL_NO_HAND
        } else {
          probability = PROB_GAP_SMALL_WITH_HAND
          if (myMax > theirMax) {
            probability += PROB_GAP_SMALL_TOTAL_ADVANTAGE
          }
          if (myInHand >= 2) {
            probability += PROB_GAP_SMALL_LARGE_HAND
          }
        }
      }
    } else {
      // TIED (after adding +2 cards, we're tied with competitor)
      if (myInHand === 0) {
        probability = PROB_TIED_NO_HAND
      } else {
        probability = PROB_TIED_BASE + (PROB_TIED_HAND_SCALING * Math.min(1, myInHand / PROB_TIED_HAND_SCALING_THRESHOLD))
      }

      // === BOARD POSITION PRIORITY BONUS ===
      // When tied, the leftmost artist on the board has a significant advantage
      // Board order: Solstice (1st) > Mira (2nd) > Atlas (3rd) > Vesper (4th) > Luxe (5th)
      const myBoardPosition = ARTISTS.indexOf(myArtist)
      const theirBoardPosition = ARTISTS.indexOf(theirArtist)
      const positionAdvantage = (theirBoardPosition - myBoardPosition) / 4  // Range: -0.8 to +0.8

      // Add board position bonus - up to 0.2 (20%) for maximum advantage
      if (positionAdvantage > 0) {
        // I have board position advantage (I'm to the left)
        probability += positionAdvantage * BOARD_POSITION_BONUS_MULTIPLIER
      } else if (positionAdvantage < 0) {
        // I have board position disadvantage (I'm to the right)
        probability += positionAdvantage * BOARD_POSITION_PENALTY_MULTIPLIER
      }

      // With +2 cards, we have significant advantage
      probability += 0.1 // Bonus for being tied but getting 2 cards

      if (myMax > theirMax + PROB_TIED_TOTAL_THRESHOLD) {
        probability += PROB_TIED_TOTAL_ADVANTAGE
      } else if (theirMax > myMax + PROB_TIED_TOTAL_THRESHOLD) {
        probability -= PROB_TIED_TOTAL_ADVANTAGE
      }
    }

    // Clamp to valid probability range
    return Math.max(MIN_PROB, Math.min(MAX_PROB, probability))
  }

  /**
   * Get cumulative value from previous rounds
   * Sums up board.artistValues[artist][0] through [currentRound-1]
   */
  private getPreviousCumulativeValue(
    board: GameBoard,
    artist: Artist,
    currentRound: number
  ): number {
    let sum = 0
    for (let r = 0; r < currentRound && r < 4; r++) {
      sum += board.artistValues[artist][r]
    }
    return sum
  }

  /**
   * Calculate expected value for THIS ROUND only
   * Uses pure math probability model
   */
  private calculateCurrentRoundEV(
    artist: Artist,
    gameState: GameState | VisibleGameState,
    aiHand: Card[],
    roundNumber: number
  ): {
    ev: number
    probabilities: CardEVResult['probabilities']
    minPossible: number
    maxPossible: number
    currentRank: number
  } {
    // Get current state
    const currentPlayed = (gameState.board.playedCards[artist] || []).length
    const inHand = aiHand.filter(c => c.artist === artist).length
    const totalInDeck = CARD_DISTRIBUTION[artist]
    const remaining = totalInDeck - currentPlayed - inHand

    // Calculate min/max possible
    const minPossible = currentPlayed + inHand
    const maxPossible = currentPlayed + inHand + remaining

    // Get current rank
    const currentRank = this.getCurrentRank(artist, gameState)

    // Get competitor information
    const competitors = this.getCompetitorStates(artist, gameState)

    // Calculate probabilities for each rank
    const probabilities = this.calculateRankProbabilities(
      artist,
      currentPlayed,
      inHand,
      remaining,
      competitors,
      minPossible,
      maxPossible
    )

    // Calculate EV using payout values
    const ev =
      probabilities.first * PAYOUT_FIRST +
      probabilities.second * PAYOUT_SECOND +
      probabilities.third * PAYOUT_THIRD +
      probabilities.fourthOrFifth * PAYOUT_FOURTH_OR_FIFTH

    return {
      ev: Math.max(0, ev),  // EV should never be negative
      probabilities,
      minPossible,
      maxPossible,
      currentRank,
    }
  }

  /**
   * Get current rank of an artist (1-5)
   * Ties broken by board position (leftmost wins)
   */
  private getCurrentRank(artist: Artist, gameState: GameState | VisibleGameState): number {
    const playedCounts: Record<Artist, number> = {} as any

    ARTISTS.forEach(a => {
      playedCounts[a] = (gameState.board.playedCards[a] || []).length
    })

    // Sort by count (descending), then by board position (ascending for tie-breaker)
    const sorted = ARTISTS.slice().sort((a, b) => {
      const countDiff = playedCounts[b] - playedCounts[a]
      if (countDiff !== 0) return countDiff
      // Tie-breaker: lower board position wins
      return ARTISTS.indexOf(a) - ARTISTS.indexOf(b)
    })

    return sorted.indexOf(artist) + 1
  }

  /**
   * Get competitor states for probability calculation
   */
  private getCompetitorStates(
    targetArtist: Artist,
    gameState: GameState | VisibleGameState
  ): Array<{
    artist: Artist
    currentCount: number
    maxPossible: number
  }> {
    return ARTISTS
      .filter(a => a !== targetArtist)
      .map(artist => {
        const currentCount = (gameState.board.playedCards[artist] || []).length
        const totalInDeck = CARD_DISTRIBUTION[artist]
        const played = currentCount
        const remaining = totalInDeck - played
        return {
          artist,
          currentCount,
          maxPossible: played + remaining,  // Worst case: all remaining get played
        }
      })
  }

  /**
   * Calculate rank probabilities using pure math
   *
   * This is a wrapper function that calls the unified calculateRankProbabilitiesUnified
   * with cardsFromAuction=1 to handle single auction scenarios.
   *
   * APPROXIMATION NOTE:
   * We approximate P(2nd) and P(3rd) from individual "beat competitor" probabilities.
   * Exact calculation would require considering all 5! = 120 permutations of finishing order,
   * which is not feasible in real-time and not necessarily more accurate.
   *
   * Key insight: Need to consider not just my potential, but competitors' potential too
   * P(I get 1st) = P(my_final > competitor1_final AND my_final > competitor2_final AND ...)
   */
  private calculateRankProbabilities(
    artist: Artist,
    currentPlayed: number,
    inHand: number,
    remaining: number,
    competitors: Array<{ artist: Artist; currentCount: number; maxPossible: number }>,
    minPossible: number,
    maxPossible: number
  ): CardEVResult['probabilities'] {
    // The unified function handles both single (+1) and double (+2) auction cases
    // For single auction, we use cardsFromAuction=1
    return this.calculateRankProbabilitiesUnified(
      artist,
      currentPlayed,
      inHand,
      remaining,
      competitors,
      minPossible,
      maxPossible,
      1  // cardsFromAuction = 1 for single auction
    )
  }

  /**
   * Calculate probability that I beat a specific competitor
   *
   * P(I beat C) = P(my_final > C_final)
   *
   * This considers:
   * - My current position advantage (most important)
   * - My hand control (cards I can play) - CRITICAL for middle positions
   * - Total card potential (played + hand + remaining)
   * - Special boost for 4-card positions (near winning)
   */
  private calculateProbabilityOfBeating(
    myCurrent: number,
    myInHand: number,
    myRemaining: number,
    theirCurrent: number,
    theirRemaining: number,
    myArtist: Artist,
    theirArtist: Artist
  ): number {
    const myMin = myCurrent + myInHand
    const myMax = myCurrent + myInHand + myRemaining
    const myTotal = myMax

    const theirMin = theirCurrent
    const theirMax = theirCurrent + theirRemaining
    const theirTotal = theirMax

    // Boundary conditions: certain win or loss
    if (myMin > theirMax) return 1.0   // My minimum > their maximum = guaranteed win
    if (myMax < theirMin) return 0.0   // My maximum < their minimum = guaranteed loss

    // Calculate advantage/gap
    const currentAdvantage = myCurrent - theirCurrent  // Positive = ahead, negative = behind
    const totalAdvantage = myTotal - theirTotal        // Total card advantage
    const gap = theirCurrent - myCurrent                // How far behind I am (positive if behind)

    let probability: number

    if (currentAdvantage > 0) {
      // === AHEAD ===
      // Exponential confidence based on lead size
      if (myCurrent >= 4) {
        // 4-card lead = dominant position
        probability = PROB_DOMINANT_FOUR_CARD
      } else {
        // Use exponential formula: confidence increases with lead
        const baseProb = BASE_CONFIDENCE + (1 - Math.exp(-currentAdvantage * DECAY_RATE)) * CONFIDENCE_RANGE

        // Hand control bonus (can extend lead)
        const handBonus = myInHand > 0
          ? HAND_BONUS_MAX * Math.min(1, myInHand / HAND_BONUS_SCALING)
          : 0

        // Total card advantage bonus (weak signal)
        const totalBonus = totalAdvantage > 0 ? TOTAL_BONUS_MAX * Math.min(1, totalAdvantage / TOTAL_BONUS_THRESHOLD) : 0

        probability = baseProb + handBonus + totalBonus
      }
    } else if (currentAdvantage < 0) {
      // === BEHIND ===
      // Probability depends on gap size AND hand control
      // CRITICAL: No hand control = severely disadvantaged (rely on opponents not playing)

      if (gap >= 3) {
        // Large gap (3+ cards) - very hard to catch up
        if (myInHand === 0) {
          probability = PROB_GAP_LARGE_NO_HAND
        } else {
          probability = PROB_GAP_LARGE_WITH_HAND
          // Only reasonable chance if have enough cards to close gap
          if (myInHand >= gap - 1 && myTotal > theirTotal) {
            probability = PROB_GAP_LARGE_WITH_ENOUGH_HAND
          }
        }
      } else if (gap === 2) {
        // Medium gap (2 cards) - difficult but possible
        if (myInHand === 0) {
          probability = PROB_GAP_MEDIUM_NO_HAND
        } else {
          probability = PROB_GAP_MEDIUM_WITH_HAND
          // Good chance if have 2+ cards and total advantage
          if (myInHand >= 2 && myTotal > theirTotal) {
            probability = PROB_GAP_MEDIUM_WITH_GOOD_HAND
          } else if (myInHand >= 1) {
            probability += PROB_GAP_MEDIUM_HAND_BOOST
          }
        }
      } else {
        // Small gap (1 card) - competitive with hand control
        if (myInHand === 0) {
          probability = PROB_GAP_SMALL_NO_HAND
        } else {
          probability = PROB_GAP_SMALL_WITH_HAND
          // Bonus for total card advantage
          if (myTotal > theirTotal) {
            probability += PROB_GAP_SMALL_TOTAL_ADVANTAGE
          }
          // Extra bonus for having 2+ cards (can overtake and potentially extend)
          if (myInHand >= 2) {
            probability += PROB_GAP_SMALL_LARGE_HAND
          }
        }
      }
    } else {
      // === TIED ===
      // Hand control is the deciding factor
      if (myInHand === 0) {
        // No hand control = slight disadvantage
        probability = PROB_TIED_NO_HAND
      } else {
        // Have hand control = advantage, scales with hand size
        probability = PROB_TIED_BASE + (PROB_TIED_HAND_SCALING * Math.min(1, myInHand / PROB_TIED_HAND_SCALING_THRESHOLD))
      }

      // === BOARD POSITION PRIORITY BONUS ===
      // When tied, the leftmost artist on the board has a significant advantage
      // Board order: Solstice (1st) > Mira (2nd) > Atlas (3rd) > Vesper (4th) > Luxe (5th)
      const myBoardPosition = ARTISTS.indexOf(myArtist)
      const theirBoardPosition = ARTISTS.indexOf(theirArtist)
      const positionAdvantage = (theirBoardPosition - myBoardPosition) / 4  // Range: -0.8 to +0.8

      // Add board position bonus - up to 0.2 (20%) for maximum advantage
      if (positionAdvantage > 0) {
        // I have board position advantage (I'm to the left)
        probability += positionAdvantage * BOARD_POSITION_BONUS_MULTIPLIER
      } else if (positionAdvantage < 0) {
        // I have board position disadvantage (I'm to the right)
        probability += positionAdvantage * BOARD_POSITION_PENALTY_MULTIPLIER
      }

      // Adjust for significant total card advantage/disadvantage
      if (myTotal > theirTotal + PROB_TIED_TOTAL_THRESHOLD) {
        probability += PROB_TIED_TOTAL_ADVANTAGE
      } else if (theirTotal > myTotal + PROB_TIED_TOTAL_THRESHOLD) {
        probability -= PROB_TIED_TOTAL_ADVANTAGE
      }
    }

    // Clamp to valid probability range
    return Math.max(MIN_PROB, Math.min(MAX_PROB, probability))
  }
}
